/* --------------------------------------------------------------------------
   efx-quote.js - T-QUOTE 2026-08-31 - "Get a quote" enquiry form.
   Draft theme #131236954194 only.

   The form works with this file absent or blocked: it is a native
   {% form 'contact' %} that POSTs to /contact by navigation. Everything here is
   enhancement only. Loaded in the head on the quote page; pulled on demand by
   efx-quote-dialog.js when the same form is opened as a modal.

     1. marks <html> so CSS may hide the conditional question
     2. mirrors radio/checkbox state onto the label (no :has() dependency)
     3. shows the fireplace-type question only for doors / screens
     4. dims the measurement fields when "I have not measured yet" is ticked
     5. reveals + drives the optional photo upload (Cloudinary unsigned preset
        'ef_fireplace' on cloud dcz6x0ccy - the SAME endpoint the live
        ef-quote-modal snippet has used in production since 2026-04; Shopify's
        contact form cannot carry attachments, so the URL travels as text)
     6. blocks a submission whose honeypot is filled
     7. keeps what the buyer typed if Shopify bounces the post back with an
        error - Liquid restores only name/email/body, so the custom fields are
        parked in sessionStorage for exactly one restore and then deleted
     8. assembles the "send photos" mailto on the success page from split parts
        so the address is not sitting in the markup for a scraper

   No submitted value is logged, echoed into the page, or sent anywhere except
   the Shopify contact endpoint and (for photos only) EF's own Cloudinary.
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('efq-js');

  /* Resolved inside boot(), not here: when the modal injects this form the
     element does not exist at parse time. T-QUOTE-DIALOG 2026-09-01. */
  var form = null;
  var STORE_KEY = 'efq:draft';

  /* -- 2. state mirrors -------------------------------------------------- */
  function syncGroup(input) {
    var name = input.name;
    var scope = form || document;
    var peers = scope.querySelectorAll('input[type="radio"][name="' + name + '"]');
    for (var i = 0; i < peers.length; i++) {
      var lab = peers[i].closest('.efq-choice');
      if (lab) lab.classList.toggle('is-checked', peers[i].checked);
    }
  }
  function bindControls(scope) {
    var boxes = scope.querySelectorAll('.efq-choice input, .efq-check input');
    for (var i = 0; i < boxes.length; i++) {
      (function (input) {
        var label = input.closest('.efq-choice') || input.closest('.efq-check');
        if (!label) return;
        function paint() {
          if (input.type === 'radio') syncGroup(input);
          else label.classList.toggle('is-checked', input.checked);
        }
        input.addEventListener('change', paint);
        input.addEventListener('focus', function () { label.classList.add('is-focus'); });
        input.addEventListener('blur', function () { label.classList.remove('is-focus'); });
        paint();
      })(boxes[i]);
    }
  }

  /* -- 3. conditional question ------------------------------------------- */
  function bindConditional() {
    var conds = form ? form.querySelectorAll('[data-efq-cond]') : [];
    if (!conds.length) return;
    for (var c = 0; c < conds.length; c++) bindOneConditional(conds[c]);
  }
  function bindOneConditional(cond) {
    var trigger = (cond.getAttribute('data-efq-cond') || '').split('|');
    function apply() {
      var picked = form.querySelector('input[name="contact[Quote for]"]:checked');
      var on = !!picked && trigger.indexOf(picked.value) !== -1;
      /* The wizard owns `hidden` on every step (it is how paging works), so
         only touch it when the wizard is not running. The state attribute is
         published either way: it is how efx-quote-wizard.js knows whether this
         question is part of the branch, and therefore what "of N" may claim. */
      cond.setAttribute('data-efq-cond-on', on ? '1' : '0');
      if (!document.documentElement.classList.contains('efq-wizard')) cond.hidden = !on;
      if (!on) {
        var chosen = cond.querySelectorAll('input:checked');
        for (var i = 0; i < chosen.length; i++) {
          chosen[i].checked = false;
          var lab = chosen[i].closest('.efq-choice');
          if (lab) lab.classList.remove('is-checked');
        }
      }
    }
    var cats = form.querySelectorAll('input[name="contact[Quote for]"]');
    for (var i = 0; i < cats.length; i++) cats[i].addEventListener('change', apply);
    apply();
  }

  /* -- 3a. category prefill (R7 island UX sage): a page that already knows what the buyer is
     looking at may open the wizard on the right tile — `/pages/get-a-quote?cat=outdoor-kitchen`
     or `data-efq-cat="outdoor-kitchen"` on the trigger (efx-quote-dialog.js copies it onto the
     form as data-efq-cat). Only applies when nothing is picked yet; a restored draft wins. */
  var CAT_SLUGS = { doors: 'Fireplace doors', door: 'Fireplace doors', screens: 'Fireplace screen', screen: 'Fireplace screen', mantels: 'Mantel shelf', mantel: 'Mantel shelf', 'gas-logs': 'Gas logs', gaslogs: 'Gas logs', fireplaces: 'Fireplace or insert', fireplace: 'Fireplace or insert', inserts: 'Fireplace or insert', 'fire-pits': 'Fire pit or outdoor kitchen', firepits: 'Fire pit or outdoor kitchen', 'outdoor-kitchen': 'Fire pit or outdoor kitchen', 'outdoor-kitchens': 'Fire pit or outdoor kitchen', islands: 'Fire pit or outdoor kitchen', bbq: 'Fire pit or outdoor kitchen', other: 'Something else' };
  function applyCategoryPrefill() {
    if (!form || form.querySelector('input[name="contact[Quote for]"]:checked')) return;
    var slug = form.getAttribute('data-efq-cat') || '';
    if (!slug) { try { slug = new URLSearchParams(location.search).get('cat') || ''; } catch (e) { slug = ''; } }
    var label = CAT_SLUGS[(slug || '').toLowerCase().trim()];
    if (!label) return;
    var input = form.querySelector('input[name="contact[Quote for]"][value="' + label + '"]');
    if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
  }

  /* -- 3b. the `spec` step (R7): one fieldset, six category groups --------
     The group whose data-efq-qcond matches the category is shown; every other
     group is hidden AND cleared, so a buyer who changes tile never posts a
     stale "Fuel: Propane" from a category that does not ask it. data-efq-when
     hides a question while contact[X] equals the given value (fire pit vs
     outdoor kitchen). data-efq-multi chips compile into one hidden contact[]
     value so the notification reads as a single English line. */
  function clearInputs(scope) {
    var els = scope.querySelectorAll('input');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.type === 'radio' || el.type === 'checkbox') {
        if (el.checked) { el.checked = false; var lab = el.closest('.efq-choice'); if (lab) lab.classList.remove('is-checked'); }
      } else if (el.hasAttribute('data-efq-multi-out')) el.value = '';
    }
  }
  function bindSpec() {
    var step = form ? form.querySelector('[data-efw-key="spec"]') : null;
    if (!step) return;
    var groups = step.querySelectorAll('[data-efq-qcond]');
    function applyWhen(group) {
      var qs = group.querySelectorAll('[data-efq-when]');
      for (var i = 0; i < qs.length; i++) {
        var q = qs[i];
        var dep = form.querySelector('input[name="' + q.getAttribute('data-efq-when') + '"]:checked');
        var off = !!dep && dep.value === q.getAttribute('data-efq-when-not');
        q.hidden = off;
        if (off) clearInputs(q);
      }
    }
    function apply() {
      var picked = form.querySelector('input[name="contact[Quote for]"]:checked');
      var cat = picked ? picked.value : '';
      for (var i = 0; i < groups.length; i++) {
        var on = groups[i].getAttribute('data-efq-qcond') === cat;
        groups[i].hidden = !on;
        if (!on) clearInputs(groups[i]); else applyWhen(groups[i]);
      }
    }
    function compile(q) {
      var out = q.querySelector('[data-efq-multi-out]');
      if (!out) return;
      var on = q.querySelectorAll('input[type="checkbox"]:checked'), vals = [];
      for (var i = 0; i < on.length; i++) vals.push(on[i].value);
      out.value = vals.join(', ');
    }
    form.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.name === 'contact[Quote for]') apply();
      if (t.name === 'efq_multi') { var q = t.closest('[data-efq-multi]'); if (q) compile(q); }
      if (t.type === 'radio') { for (var i = 0; i < groups.length; i++) if (!groups[i].hidden) applyWhen(groups[i]); }
    });
    apply();
    var multis = step.querySelectorAll('[data-efq-multi]');
    for (var m = 0; m < multis.length; m++) compile(multis[m]);
  }

  /* -- 3c. "When may we call you?" — the phone hint states a consequence, not an error */
  function bindCallWindow() {
    var hint = form ? form.querySelector('[data-efq-phonehint]') : null;
    if (!hint) return;
    function apply() {
      var picked = form.querySelector('input[name="contact[Call window]"]:checked');
      var wantsCall = !!picked && picked.hasAttribute('data-efq-callopt');
      var phone = form.querySelector('#efq-phone');
      hint.textContent = (wantsCall && phone && !phone.value.trim())
        ? hint.getAttribute('data-efq-phonehint-call')
        : hint.getAttribute('data-efq-phonehint-default');
    }
    form.addEventListener('change', function (e) { if (e.target && e.target.name === 'contact[Call window]') apply(); });
    var phone = form.querySelector('#efq-phone');
    if (phone) phone.addEventListener('input', apply);
    apply();
  }

  /* -- 3d. reference code: EF-Q + 8 base32 chars, made once, parked in the draft,
     posted to the inbox and echoed on the confirmation. The buyer leaves with
     something to quote back, without EF promising an email it cannot send. */
  function ensureReference() {
    var ref = form ? form.querySelector('[data-efq-ref]') : null;
    if (!ref || ref.value) return;
    var alpha = 'ABCDEFGHJKMNPQRSTVWXYZ23456789', out = 'EF-Q';
    var buf = new Uint8Array(8);
    if (window.crypto && crypto.getRandomValues) crypto.getRandomValues(buf); else for (var j = 0; j < 8; j++) buf[j] = Math.floor(Math.random() * 256);
    for (var i = 0; i < 8; i++) out += alpha[buf[i] % alpha.length];
    ref.value = out;
  }

  /* -- 4. "not measured yet" --------------------------------------------- */
  function bindSizes() {
    var wrap = document.querySelector('[data-efq-sizes]');
    if (!wrap) return;
    var flag = wrap.querySelector('[data-efq-nomeasure]');
    var fields = wrap.querySelectorAll('.efq-input');
    if (!flag) return;
    function apply() {
      wrap.classList.toggle('is-off', flag.checked);
      for (var i = 0; i < fields.length; i++) {
        fields[i].readOnly = flag.checked;
        if (flag.checked) fields[i].value = '';
      }
    }
    flag.addEventListener('change', apply);
    apply();
  }

  /* -- 5. photo upload --------------------------------------------------- */
  var CLOUD = 'https://api.cloudinary.com/v1_1/dcz6x0ccy/image/upload';
  var PRESET = 'ef_fireplace';
  var FOLDER = 'fireplace-photos';
  var MAX_FILES = 3;
  var MAX_BYTES = 10 * 1024 * 1024;

  function bindPhotos() {
    var block = document.querySelector('[data-efq-photo]');
    if (!block || !window.FormData || !window.fetch) return;
    block.hidden = false;

    var input = block.querySelector('input[type="file"]');
    var drop = block.querySelector('.efq-drop');
    var list = block.querySelector('.efq-thumbs');
    var status = block.querySelector('.efq-photo__status');
    var field = block.querySelector('input[type="hidden"]');
    var urls = [];
    /* The wizard counts this step only when this attribute is present: with no
       fetch/FormData there is no upload, so there is no question to ask and
       "Step 4 of 7" would be a lie. */
    block.setAttribute('data-efq-photo-ready', '1');

    function say(msg, err) {
      status.textContent = msg || '';
      status.classList.toggle('is-err', !!err);
    }
    function commit() {
      field.value = urls.join('  ');
      say(urls.length ? urls.length + (urls.length === 1 ? ' photo attached.' : ' photos attached.') : '');
    }
    function addThumb(localSrc) {
      var li = document.createElement('li');
      var img = document.createElement('img');
      img.src = localSrc;
      img.alt = '';
      li.appendChild(img);
      var kill = document.createElement('button');
      kill.type = 'button';
      kill.setAttribute('aria-label', 'Remove this photo');
      kill.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      kill.addEventListener('click', function () {
        var at = urls.indexOf(li.getAttribute('data-url') || '');
        if (at > -1) urls.splice(at, 1);
        li.remove();
        commit();
      });
      li.appendChild(kill);
      list.appendChild(li);
      return li;
    }
    function upload(file) {
      if (urls.length >= MAX_FILES) { say('Three photos is plenty - remove one to add another.', true); return; }
      if (!/^image\//.test(file.type)) { say('That is not an image file.', true); return; }
      if (file.size > MAX_BYTES) { say('That photo is over 10 MB - a phone photo straight from the camera roll is fine.', true); return; }

      var localSrc = URL.createObjectURL(file);
      var li = addThumb(localSrc);
      li.classList.add('is-busy');
      say('Uploading...');

      var fd = new FormData();
      fd.append('file', file);
      fd.append('upload_preset', PRESET);
      fd.append('folder', FOLDER);
      fetch(CLOUD, { method: 'POST', body: fd })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (!data || !data.secure_url) throw new Error('no url');
          urls.push(data.secure_url);
          li.classList.remove('is-busy');
          li.setAttribute('data-url', data.secure_url);
          commit();
        })
        .catch(function () {
          li.remove();
          say('That photo would not upload. Send it in your reply to our email instead - the request itself is unaffected.', true);
        });
    }
    function take(files) {
      for (var i = 0; i < files.length && i < MAX_FILES; i++) upload(files[i]);
    }

    input.addEventListener('change', function () { if (input.files) take(input.files); input.value = ''; });
    drop.addEventListener('click', function () { input.click(); });
    ['dragenter', 'dragover'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('is-over'); });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('is-over'); });
    });
    drop.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) take(e.dataTransfer.files);
    });
  }

  /* -- 6/7. submit guard + one-shot draft --------------------------------- */
  function readable(el) {
    return el.name && el.name.indexOf('contact[') === 0 && (el.type !== 'hidden' || el.hasAttribute('data-efq-ref') || el.hasAttribute('data-efq-schedule-intent') || el.hasAttribute('data-efq-multi-out') || el.name === 'contact[Photos]');
  }
  function saveDraft() {
    if (!form || !window.sessionStorage) return;
    try {
      var bag = {};
      var els = form.elements;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (!readable(el)) continue;
        if (el.type === 'radio' || el.type === 'checkbox') { if (el.checked) bag[el.name] = el.value; }
        else if (el.value) bag[el.name] = el.value;
      }
      /* Not a contact[] field, so it never travels to Shopify - it is only how
         efx-quote-wizard.js lands them back on the question they were answering
         after a bounced post, a refresh or an accidental close. */
      var at = form.getAttribute('data-efw-at');
      if (at) bag.__step = at;
      sessionStorage.setItem(STORE_KEY, JSON.stringify(bag));
    } catch (e) { /* private mode - the draft is a convenience, not a contract */ }
  }
  function restoreDraft() {
    if (!form || !window.sessionStorage) return;
    var raw;
    try { raw = sessionStorage.getItem(STORE_KEY); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
    var bag;
    try { bag = JSON.parse(raw); } catch (e) { return; }
    if (bag.__step) { form.setAttribute('data-efw-restore', bag.__step); delete bag.__step; }
    Object.keys(bag).forEach(function (name) {
      var nodes = form.querySelectorAll('[name="' + name + '"]');
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el.type === 'radio' || el.type === 'checkbox') { if (el.value === bag[name]) el.checked = true; }
        else if (!el.value) el.value = bag[name];
      }
    });
    /* hidden chips: re-tick the boxes the compiled value names */
    var multis = form.querySelectorAll('[data-efq-multi]');
    for (var m = 0; m < multis.length; m++) {
      var out = multis[m].querySelector('[data-efq-multi-out]');
      if (!out || !out.value) continue;
      var vals = out.value.split(', ');
      var boxes = multis[m].querySelectorAll('input[type="checkbox"]');
      for (var k = 0; k < boxes.length; k++) if (vals.indexOf(boxes[k].value) !== -1) boxes[k].checked = true;
    }
  }
  function peekDraft() {
    if (!window.sessionStorage) return null;
    try { var raw = sessionStorage.getItem(STORE_KEY); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  }
  function dropDraft() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* noop */ }
  }

  function bindSubmit() {
    if (!form) return;
    var boundForm = form;
    var submitting = false;
    function unlock() {
      submitting = false;
      boundForm.removeAttribute('aria-busy');
      var buttons = boundForm.querySelectorAll('[data-efq-submit-locked]');
      for (var i = 0; i < buttons.length; i++) { buttons[i].disabled = false; buttons[i].removeAttribute('data-efq-submit-locked'); }
    }
    /* Save the validated action before Shopify's CAPTCHA handoff. Its native
       submit replay does not emit another SubmitEvent. */
    function prepareSubmit(e) {
      if (e.target !== boundForm || e.defaultPrevented) return;
      var hp = boundForm.querySelector('[name="efq_hp"]');
      if ((hp && hp.value) || submitting) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      var followup = boundForm.querySelector('[data-efq-schedule-intent]');
      var schedule = !!(e.submitter && e.submitter.hasAttribute('data-efq-schedule-send'));
      if (followup) followup.value = schedule ? 'Schedule a call — booking pending' : '';
      saveDraft();
      var ref = boundForm.querySelector('[data-efq-ref]');
      try {
        if (schedule) sessionStorage.setItem('efq:booking-pending', ref ? ref.value : '');
        else sessionStorage.removeItem('efq:booking-pending');
      } catch (err) { /* The confirmation still offers a manual booking link. */ }
      submitting = true;
      boundForm.setAttribute('aria-busy', 'true');
      var buttons = boundForm.querySelectorAll('button[type="submit"]');
      for (var i = 0; i < buttons.length; i++) if (!buttons[i].disabled) {
        buttons[i].setAttribute('data-efq-submit-locked', ''); buttons[i].disabled = true;
      }
      boundForm.dispatchEvent(new CustomEvent('efq:submit-prepared'));
      /* A platform handler may pause for verification, then submit natively.
         Keep the saved intent, but release our UI lock if it owns this attempt
         so a canceled/failed verification does not leave dead buttons. */
      window.setTimeout(function () { if (e.defaultPrevented) unlock(); }, 0);
    }
    boundForm.addEventListener('efq:validated-submit', function (e) { prepareSubmit(e.detail.submitEvent); });
    boundForm.addEventListener('submit', function (e) {
      if (boundForm.hasAttribute('data-efw-booted')) return;
      if (!boundForm.checkValidity()) { e.preventDefault(); e.stopImmediatePropagation(); boundForm.reportValidity(); return; }
      prepareSubmit(e);
    }, true);
    window.addEventListener('pageshow', function (e) { if (e.persisted) unlock(); });
  }

  /* -- 8. success-page mailto -------------------------------------------- */
  function bindMailto() {
    var nodes = document.querySelectorAll('[data-efq-mail]');
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      if (!a.getAttribute('data-u')) continue;   /* already assembled */
      var addr = a.getAttribute('data-u') + '@' + a.getAttribute('data-d');
      a.href = 'mailto:' + addr + '?subject=' + encodeURIComponent(a.getAttribute('data-s') || '');
      a.removeAttribute('data-u');
      a.removeAttribute('data-d');
    }
  }


  /* -- 8c. lead signal (EF_QUOTE_LEAD_20260916) ------------------------------------------
     Owner 2026-09-16: "podlacz [get a quote] jako conversion lead". Fires ONLY
     from server-rendered success markup (bindDone), once per reference:
     Google Ads conversion 7773149729 "EF Quote Request (Get a quote form)" -
     SECONDARY, $250 default, transaction_id = the request reference so a reload
     cannot count twice - plus a GA4 event and a Shopify custom event any web
     pixel (Microsoft UET) may subscribe to. gtag() here is the consent stub from
     snippets/ef-analytics.liquid: without marketing consent the stub itself
     drops the 'conversion' event and nothing leaves the page. */
  function trackQuote(bag) {
    var ref = bag['contact[Reference]'] || '';
    var cat = bag['contact[Quote for]'] || '';
    var key = 'efq:lead:' + (ref || 'noref');
    try { if (sessionStorage.getItem(key) === '1') return; sessionStorage.setItem(key, '1'); } catch (e) { /* private mode: once per load */ }
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'ef_quote_submitted', { event_category: 'lead', quote_category: cat, quote_reference: ref, send_to: 'G-LJCQPD1VSH' });
        window.gtag('event', 'conversion', { send_to: 'AW-666465896/xvzJCKG0w_ocEOjs5b0C', value: 250, currency: 'USD', transaction_id: ref });
      }
    } catch (e) { /* analytics must never break the confirmation */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('ef_quote_submitted', { category: cat, reference: ref, value: 250, currency: 'USD' });
      }
    } catch (e) { /* noop */ }
  }

  /* -- 8b. confirmation (R7): reference + copy + booking state, from the draft.
     ORDER RULE: read the draft → build everything → THEN drop it. Before R7 the
     draft was cleared first and the confirmation knew nothing about the request. */
  function loadCalendly(cb) {
    if (window.Calendly) { cb(); return; }
    if (!document.querySelector('link[href*="assets.calendly.com"]')) {
      var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = 'https://assets.calendly.com/assets/external/widget.css'; document.head.appendChild(l);
    }
    if (!document.querySelector('script[src*="assets.calendly.com/assets/external/widget.js"]')) {
      var sc = document.createElement('script'); sc.src = 'https://assets.calendly.com/assets/external/widget.js'; sc.async = true; document.body.appendChild(sc);
    }
    var n = 0, t = setInterval(function () { if (window.Calendly) { clearInterval(t); cb(); } else if (++n > 80) { clearInterval(t); cb(); } }, 50);
  }
  function bindDone(done) {
    if (done.getAttribute('data-efq-done-bound') === '1') return;
    done.setAttribute('data-efq-done-bound', '1');
    var bag = peekDraft() || {};
    trackQuote(bag);
    var ref = bag['contact[Reference]'] || '';
    var refLine = done.querySelector('[data-efq-refline]'), refCode = done.querySelector('[data-efq-refcode]');
    if (ref && refLine && refCode) { refCode.textContent = ref; refLine.hidden = false; }

    /* the copy: a mailto to the buyer's own inbox is the only "email" EF can offer today */
    var copyLine = done.querySelector('[data-efq-copyline]'), copy = done.querySelector('[data-efq-copy]');
    var email = bag['contact[email]'] || '';
    if (copyLine && copy && email) {
      var lines = [], skip = { 'contact[email]': 1, 'contact[name]': 1, 'contact[phone]': 1, 'contact[tags]': 1, '__step': 1 };
      Object.keys(bag).forEach(function (k) {
        if (skip[k] || !bag[k]) return;
        var label = k.replace(/^contact\[/, '').replace(/\]$/, '');
        if (label === 'body') label = 'Notes';
        lines.push(label + ': ' + bag[k]);
      });
      var subject = 'My quote request to ExceptionalFire' + (ref ? ' — ' + ref : '');
      var body = 'Copy of what I sent to ExceptionalFire' + (ref ? ' (reference ' + ref + ')' : '') + ':\n\n' + lines.join('\n') + '\n\nA specialist replies within one business day. (949) 619-7824';
      copy.href = 'mailto:' + encodeURIComponent(email) + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
      copyLine.hidden = false;
    }

    /* photos already attached → the photo panel has nothing to ask for */
    var panel = done.querySelector('[data-efq-photopanel]');
    if (panel && bag['contact[Photos]']) panel.hidden = true;

    /* booking state A / B / C (C wins over B); "Email only" demotes the button to a link */
    var book = done.querySelector('[data-efq-book]');
    if (!book) return;
    var win = bag['contact[Call window]'] || '';
    var notMeasured = bag['contact[Measured]'] === 'Not measured yet';
    var state = notMeasured ? 'C' : (win && win !== 'Email only, please' ? 'B' : 'A');
    var copyEl = book.querySelector('[data-efq-book-copy]'), labelEl = book.querySelector('[data-efq-book-label]'), btn = book.querySelector('[data-efq-book-btn]');
    var txt = book.getAttribute('data-efq-copy-' + state.toLowerCase()) || '';
    if (state === 'B') txt = txt.replace('{window}', win === 'Any time in business hours' ? 'in business hours' : 'in the ' + win.toLowerCase());
    if (copyEl) copyEl.textContent = txt;
    if (labelEl) labelEl.textContent = book.getAttribute('data-efq-label-' + state.toLowerCase()) || 'Pick a time';
    book.setAttribute('data-efq-book-state', state);
    if (win === 'Email only, please' && btn) { btn.className = 'efx-link efq-book__link'; }

    /* prefill: Calendly reads name/email/a1 from the query string (documented) */
    var base = book.getAttribute('data-efq-book-url') || (btn && btn.href);
    var qs = [];
    if (bag['contact[name]']) qs.push('name=' + encodeURIComponent(bag['contact[name]']));
    if (email) qs.push('email=' + encodeURIComponent(email));
    var a1 = (bag['contact[Quote for]'] || '') + (ref ? ' · ' + ref : '');
    if (a1) qs.push('a1=' + encodeURIComponent(a1));
    qs.push('utm_source=quote-confirmation');
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + qs.join('&');
    if (btn) {
      btn.href = url;
      var bookingOpening = false;
      function openBooking() {
        if (bookingOpening) return;
        bookingOpening = true;
        var sawOverlay = false;
        var observer = new MutationObserver(function () {
          var overlay = document.querySelector('.calendly-overlay');
          if (overlay) sawOverlay = true;
          else if (sawOverlay) { bookingOpening = false; observer.disconnect(); }
        });
        observer.observe(document.body, { childList: true });
        window.setTimeout(function () { if (!sawOverlay) { bookingOpening = false; observer.disconnect(); } }, 6000);
        var openIt = function () {
          if (window.EFXCalendly && typeof window.EFXCalendly.open === 'function') {
            window.EFXCalendly.open(btn);
            return;
          }
          loadCalendly(function () {
            if (window.Calendly && Calendly.initPopupWidget) Calendly.initPopupWidget({ url: url });
            else { bookingOpening = false; observer.disconnect(); var popup = window.open(url, '_blank', 'noopener'); if (!popup) window.location.href = url; }
          });
        };
        var dlg = done.closest ? done.closest('dialog') : null;
        if (dlg && dlg.open) {
          if (window.EFQuoteDialog) window.EFQuoteDialog.close();
          else if (typeof dlg.close === 'function') dlg.close();
          window.setTimeout(openIt, 260);
        } else openIt();
      }
      /* Window capture precedes the shared document handler: close the native
         quote dialog before Calendly opens outside its top layer. */
      window.addEventListener('click', function (e) {
        if (!btn.contains(e.target) || e.button === 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault(); e.stopPropagation(); openBooking();
      }, true);
      window.addEventListener('message', function (e) {
        if (e.source === window && e.origin === window.location.origin && e.data && e.data.event === 'calendly.popup_closed') bookingOpening = false;
      });
      var wantsSchedule = false;
      try {
        wantsSchedule = bag['contact[Follow-up]'] === 'Schedule a call — booking pending' && !!ref && sessionStorage.getItem('efq:booking-pending') === ref;
        sessionStorage.removeItem('efq:booking-pending');
      } catch (err) { /* Manual booking remains available. */ }
      /* Only server-rendered successful-contact markup reaches bindDone. */
      if (wantsSchedule) {
        if (copyEl) copyEl.textContent = 'Your quote request has been sent. Now choose a time for your call.';
        if (labelEl) labelEl.textContent = 'Choose a call time';
        window.setTimeout(openBooking, 0);
      }
    }
  }

  function boot() {
    form = document.getElementById('efq-form');
    var done = document.querySelector('[data-efq-done]');
    if (done) { bindDone(done); dropDraft(); bindMailto(); return; }
    if (form && form.getAttribute('data-efq-booted') !== '1') {
      form.setAttribute('data-efq-booted', '1');
      bindPhotos();
      restoreDraft();     /* values first, so the paint below reflects them */
      applyCategoryPrefill();   /* ?cat=… or data-efq-cat on the opener, before the branch binds */
      bindControls(form);
      bindConditional();
      bindSpec();
      bindCallWindow();
      ensureReference();
      bindSizes();
      bindSubmit();
      /* Enhancement on top of an enhancement: the wizard re-parents this same
         markup into a paged frame. It may be absent (blocked, failed, older
         cache) and the long form below is then exactly what the buyer gets. */
      if (window.EFQuoteWizard && typeof window.EFQuoteWizard.boot === 'function') {
        window.EFQuoteWizard.boot(form);
      }
    }
    bindMailto();
  }

  /* An accidental close, a stray Back, a refresh: park what is typed so the one
     shot restore above can put it back. Costs nothing when nothing was typed. */
  window.addEventListener('pagehide', function () {
    if (form && !document.querySelector('[data-efq-done]')) saveDraft();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* T-QUOTE-DIALOG 2026-09-01: assets/efx-quote-dialog.js injects this same form
     into the modal long after this file has run, so boot() has to be callable
     again. It is idempotent - a booted form carries data-efq-booted - and it is
     the ONLY way the modal gets behaviour, so the page and the modal can never
     drift apart. */
  window.EFQuoteForm = { boot: boot, save: saveDraft, drop: dropDraft };
})();
